import { ApiError, apiDelete, apiGet, apiGetPaginated, apiPatch, apiPost, apiPostFormData, apiUploadFormData, requireApiData } from '@/lib/api/client';
import { AUTH_STORAGE_KEYS } from '@/lib/api/config';
import type {
  EmployeeApiRecord,
  EmployeeStatsApi,
  ListEmployeesParams,
  PaginationMeta,
  TeamRoster,
  TransferTeamMembersBody,
  TransferTeamMembersResult,
} from '@/types/employee';

function toQueryParams(params: ListEmployeesParams): Record<string, string> {
  const q: Record<string, string> = {};
  if (params.page != null) q.page = String(params.page);
  if (params.limit != null) q.limit = String(params.limit);
  if (params.search?.trim()) q.search = params.search.trim();
  if (params.departmentId) q.departmentId = params.departmentId;
  if (params.reportingManagerId) q.reportingManagerId = params.reportingManagerId;
  if (params.status) q.status = params.status;
  if (params.employmentType) q.employmentType = params.employmentType;
  if (params.locationId) q.locationId = params.locationId;
  if (params.sortBy) q.sortBy = params.sortBy;
  if (params.sortOrder) q.sortOrder = params.sortOrder;
  return q;
}


type Address = {
  line1: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
};

export async function listEmployees(
  params: ListEmployeesParams = {},
): Promise<{ data: EmployeeApiRecord[]; pagination: PaginationMeta }> {
  return apiGetPaginated<EmployeeApiRecord>('/employees', toQueryParams(params));
}

export async function listTeams(params: {
  scope?: 'my' | 'all';
  departmentId?: string;
  search?: string;
  attendance?: string;
} = {}): Promise<TeamRoster> {
  const q: Record<string, string> = {};
  if (params.scope) q.scope = params.scope;
  if (params.departmentId) q.departmentId = params.departmentId;
  if (params.search?.trim()) q.search = params.search.trim();
  if (params.attendance) q.attendance = params.attendance;
  const env = await apiGet<TeamRoster>('/employees/teams', q);
  return requireApiData(env.data, 'Team roster returned no data');
}

export async function transferTeamMembers(
  body: TransferTeamMembersBody,
): Promise<TransferTeamMembersResult> {
  try {
    const env = await apiPost<TransferTeamMembersResult>('/employees/reassign-team', body);
    return requireApiData(env.data, 'Team transfer returned no data');
  } catch (err) {
    const status = err instanceof ApiError ? err.status : 0;
    if (status !== 404) throw err;
    const employees: TransferTeamMembersResult['employees'] = [];
    for (const id of body.employeeIds) {
      const updated = await updateEmployee(id, {
        reportingManagerId: body.reportingManagerId,
        ...(body.departmentId ? { departmentId: body.departmentId } : {}),
      });
      employees.push({
        id: updated.id,
        reportingManagerId: updated.reportingManagerId ?? body.reportingManagerId,
        departmentId: updated.department?.id ?? body.departmentId ?? '',
      });
    }
    return {
      moved: employees.length,
      reportingManagerId: body.reportingManagerId,
      departmentId: body.departmentId ?? null,
      employees,
    };
  }
}

/** Request body for POST /employees (matches HRM CreateEmployeeSchema). */
export type CreateEmployeeBody = {
  employeeId?: string;
  /** Optional — resolved from work email or auto-provisioned when omitted. */
  userId?: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  displayName?: string;
  title?: string;
  dateOfBirth: string;
  gender?: string;
  bloodGroup?: string;
  maritalStatus?: string;
  nationality?: string;
  personalEmail?: string;
  personalPhone?: string;
  designation: string;
  departmentId: string;
  locationId?: string;
  reportingManagerId?: string;
  employmentType?: 'full-time' | 'part-time' | 'contract' | string;
  joiningDate: string;
  workEmail: string;
  workPhone?: string;
  probationPeriod?: number;
  noticePeriod?: number;
  skills?: string[];
  certifications?: string[];
  notes?: string;
  /** When true (default), backend starts the standard onboarding checklist. */
  startOnboarding?: boolean;
  currentAddress?: Address | null;
};

export type CreateEmployeeOnboardingCase = {
  id: string;
  status: string;
  tasks?: { id: string; title: string; ownerRole: string; status: string }[];
};

export type CreateEmployeeResult = EmployeeApiRecord & {
  onboardingCase?: CreateEmployeeOnboardingCase | null;
};

export async function createEmployee(body: CreateEmployeeBody): Promise<CreateEmployeeResult> {
  const env = await apiPost<CreateEmployeeResult>('/employees', body);
  return requireApiData(env.data, 'Create employee returned no data');
}

export type UpdateEmployeeBody = {
  employeeId?: string;
  workEmail?: string;
  displayName?: string;
  title?: string;
  designation?: string;
  departmentId?: string;
  locationId?: string | null;
  reportingManagerId?: string | null;
  employmentType?: string;
  workPhone?: string;
  skills?: string[];
  certifications?: string[];
  notes?: string;
  gender?: string;
  bloodGroup?: string;
  maritalStatus?: string;
  nationality?: string;
  personalEmail?: string;
  personalPhone?: string;
  panNumber?: string;
  aadharNumber?: string;
  passportNumber?: string;
  passportExpiryDate?: string;
  drivingLicenseNumber?: string;
  drivingLicenseExpiryDate?: string;
  probationPeriod?: number;
  noticePeriod?: number;
  currentAddress?:Address | null;
};

export async function getEmployee(employeePk: string): Promise<EmployeeApiRecord> {
  const env = await apiGet<EmployeeApiRecord>(`/employees/${encodeURIComponent(employeePk)}`);
  return requireApiData(env.data, 'Employee not found');
}

export async function updateEmployee(
  employeePk: string,
  body: UpdateEmployeeBody,
): Promise<EmployeeApiRecord> {
  const env = await apiPatch<EmployeeApiRecord>(`/employees/${encodeURIComponent(employeePk)}`, body);
  return requireApiData(env.data, 'Update employee returned no data');
}

/** Own profile. Work email, department, and designation stay on the HR update. */
export type UpdateMyEmployeeBody = Omit<
  UpdateEmployeeBody,
  'workEmail' | 'departmentId' | 'designation'
>;

export async function updateMyEmployee(body: UpdateMyEmployeeBody): Promise<EmployeeApiRecord> {
  const env = await apiPatch<EmployeeApiRecord>('/employees/me', body);
  return requireApiData(env.data, 'Update profile returned no data');
}

export async function toggleEmployeeActiveStatus(
  employeePk: string,
  isActive: boolean,
): Promise<EmployeeApiRecord> {
  const env = await apiPatch<EmployeeApiRecord>(`/employees/${encodeURIComponent(employeePk)}`, { isActive });
  return requireApiData(env.data, 'Update status returned no data');
}

export type EmployeeExitEmbedded = {
  id: string;
  firstName: string;
  lastName: string;
  designation: string;
  joiningDate: string;
  resignationDate?: string | null;
  lastWorkingDay?: string | null;
  department?: { id: string; name: string; code?: string | null } | null;
  status?: { option_code?: string; option_name?: string | null } | null;
};

export type EmployeeExitAnalyticsRow = {
  id: string;
  orgId: string;
  employeeId: string;
  lastWorkingDate?: string | null;
  reasonTypeName?: string | null;
  exitInterviewConducted?: string | null;
  thinkTheOrganizationDoToImprove?: string | null;
  whatDidYouLikeTheMost?: string | null;
  anythingYouWishToShare?: string | null;
  employee?: EmployeeExitEmbedded | null;
};

export async function listEmployeeExitsAnalytics(): Promise<EmployeeExitAnalyticsRow[]> {
  const env = await apiGet<EmployeeExitAnalyticsRow[]>('/employees/exits');
  return Array.isArray(env.data) ? env.data : [];
}

/** Page through /employees (max 100 per page) for org chart and similar. */
export async function fetchAllEmployeesForOrgChart(
  params: Omit<ListEmployeesParams, 'page' | 'limit'> = {},
): Promise<EmployeeApiRecord[]> {
  const acc: EmployeeApiRecord[] = [];
  let page = 1;
  const limit = 100;
  for (;;) {
    const { data, pagination } = await listEmployees({
      ...params,
      page,
      limit,
      sortBy: 'firstName',
      sortOrder: 'asc',
    });
    acc.push(...data);
    const totalPages = pagination.totalPages ?? 1;
    if (!data.length || page >= totalPages) break;
    page++;
    if (page > 200) break;
  }
  return acc;
}

function authUserIdFromAccessToken(): string | null {
  const token = localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1] ?? '')) as { sub?: string };
    return typeof payload.sub === 'string' ? payload.sub : null;
  } catch {
    return null;
  }
}

export async function uploadMyEmployeeImages(input: {
  profileImage?: File;
  coverImage?: File;
}): Promise<EmployeeApiRecord> {
  const form = new FormData();
  if (input.profileImage) {
    form.append('profile_image', input.profileImage);
  }
  if (input.coverImage) {
    form.append('cover_image', input.coverImage);
  }
  const envelope = await apiUploadFormData<EmployeeApiRecord>('/employees/me/images', form);
  return requireApiData(envelope.data, 'Employee image upload returned no data');
}

/** Upload profile/cover images for a specific employee (HR directory add/edit). */
export async function uploadEmployeeImages(
  employeeId: string,
  input: {
    profileImage?: File;
    coverImage?: File;
  },
): Promise<EmployeeApiRecord> {
  const form = new FormData();
  if (input.profileImage) {
    form.append('profile_image', input.profileImage);
  }
  if (input.coverImage) {
    form.append('cover_image', input.coverImage);
  }
  const envelope = await apiUploadFormData<EmployeeApiRecord>(
    `/employees/${encodeURIComponent(employeeId)}/images`,
    form,
  );
  return requireApiData(envelope.data, 'Employee image upload returned no data');
}

/** Current user's HRM employee profile (JWT user id, then /employees/me). */
export async function getMyEmployee(): Promise<EmployeeApiRecord | null> {
  const userId = authUserIdFromAccessToken();
  if (userId) {
    const byUser = await getEmployeeByUserId(userId);
    if (byUser) return byUser;
  }

  try {
    const env = await apiGet<EmployeeApiRecord>('/employees/me');
    return env.data ?? null;
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) {
      return null;
    }
    throw e;
  }
}

/** Create a linked employee profile for the current login if missing (punch / self-service). */
export async function ensureMyEmployee(input?: {
  firstName?: string;
  lastName?: string;
  designation?: string;
  departmentId?: string;
  locationId?: string;
}): Promise<EmployeeApiRecord> {
  const res = await apiPost<EmployeeApiRecord>('/employees/me/ensure', input ?? {});
  return requireApiData(res.data, 'Failed to link employee profile');
}

/** HRM employee row for the current auth user, or null if no linked employee (404). */
export async function getEmployeeByUserId(userId: string): Promise<EmployeeApiRecord | null> {
  try {
    const env = await apiGet<EmployeeApiRecord>(
      `/employees/by-user/${encodeURIComponent(userId)}`,
    );
    return env.data ?? null;
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) {
      return null;
    }
    throw e;
  }
}

export async function getEmployeeStats(): Promise<EmployeeStatsApi> {
  const envelope = await apiGet<EmployeeStatsApi>('/employees/stats');
  if (!envelope.data) {
    throw new Error('Employee statistics returned no data');
  }
  return envelope.data;
}

/** Response from POST /mobile/files/upload */
export interface MobileFileUploadResult {
  fileUrl: string;
  fileName?: string;
  mimeType?: string;
  fileSize?: number;
  size?: number;
  [key: string]: unknown;
}

export async function uploadMobileFile(file: File): Promise<MobileFileUploadResult> {
  const form = new FormData();
  form.append('file', file);
  const envelope = await apiPostFormData<MobileFileUploadResult>('/mobile/files/upload', form);
  return requireApiData(envelope.data, 'File upload returned no data');
}

/** Employee-uploaded document rows (HRM employee profile). */
export interface EmployeeDocumentApiRow {
  id: string;
  employeeId?: string;
  documentType?: string | null;
  title?: string | null;
  fileName?: string | null;
  fileUrl?: string | null;
  mimeType?: string | null;
  fileSize?: number | null;
  isVerified?: boolean | null;
  status?: string | null;
  metadata?: Record<string, unknown> | null;
  createdAt?: string;
  [key: string]: unknown;
}

export type CreateEmployeeDocumentBody = {
  documentType: string;
  fileName: string;
  fileUrl: string;
  mimeType?: string;
  fileSize?: number;
  title?: string;
  relatedEntityType?: 'education' | 'experience';
  relatedEntityId?: string;
  metadata?: Record<string, unknown>;
};

export type UpdateEmployeeDocumentBody = {
  isVerified?: boolean;
  metadata?: Record<string, unknown>;
  documentType?: string;
  fileName?: string;
  fileUrl?: string;
};

export async function listEmployeeDocuments(
  employeeId: string,
): Promise<EmployeeDocumentApiRow[]> {
  const envelope = await apiGet<EmployeeDocumentApiRow[]>(
    `/employees/${encodeURIComponent(employeeId)}/documents`,
  );
  return envelope.data ?? [];
}

export async function addEmployeeDocument(
  employeeId: string,
  body: CreateEmployeeDocumentBody,
): Promise<EmployeeDocumentApiRow> {
  const envelope = await apiPost<EmployeeDocumentApiRow>(
    `/employees/${encodeURIComponent(employeeId)}/documents`,
    body,
  );
  return requireApiData(envelope.data, 'Failed to add document');
}

export async function updateEmployeeDocument(
  employeeId: string,
  documentId: string,
  body: UpdateEmployeeDocumentBody,
): Promise<EmployeeDocumentApiRow> {
  const envelope = await apiPatch<EmployeeDocumentApiRow>(
    `/employees/${encodeURIComponent(employeeId)}/documents/${encodeURIComponent(documentId)}`,
    body,
  );
  return requireApiData(envelope.data, 'Failed to update document');
}

export async function deleteEmployeeDocument(
  employeeId: string,
  documentId: string,
): Promise<void> {
  await apiDelete(
    `/employees/${encodeURIComponent(employeeId)}/documents/${encodeURIComponent(documentId)}`,
  );
}

export type CreateEmployeeEducationBody = {
  courseName: string;
  courseType?: string;
  institutionName: string;
  boardUniversity?: string;
  mainSubjects?: string;
  yearOfCompletion?: string;
  score?: string;
  additionalNotes?: string;
};

export type EmployeeEducationApiRow = {
  id: string;
  employeeId: string;
  courseName: string;
  courseType?: string | null;
  institutionName: string;
  boardUniversity?: string | null;
  mainSubjects?: string | null;
  yearOfCompletion?: string | null;
  score?: string | null;
  additionalNotes?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

export async function addEmployeeEducation(
  employeeId: string,
  body: CreateEmployeeEducationBody,
): Promise<EmployeeEducationApiRow> {
  const env = await apiPost<EmployeeEducationApiRow>(
    `/employees/${encodeURIComponent(employeeId)}/educations`,
    body,
  );
  return requireApiData(env.data, 'Failed to add education record');
}

export async function listEmployeeEducations(
  employeeId: string,
): Promise<EmployeeEducationApiRow[]> {
  const env = await apiGet<EmployeeEducationApiRow[]>(
    `/employees/${encodeURIComponent(employeeId)}/educations`,
  );
  return env.data ?? [];
}

export type UpdateEmployeeEducationBody = Partial<CreateEmployeeEducationBody>;

export async function updateEmployeeEducation(
  employeeId: string,
  educationId: string,
  body: UpdateEmployeeEducationBody,
): Promise<EmployeeEducationApiRow> {
  const env = await apiPatch<EmployeeEducationApiRow>(
    `/employees/${encodeURIComponent(employeeId)}/educations/${encodeURIComponent(educationId)}`,
    body,
  );
  return requireApiData(env.data, 'Failed to update education record');
}

export async function deleteEmployeeEducation(
  employeeId: string,
  educationId: string,
): Promise<void> {
  await apiDelete(
    `/employees/${encodeURIComponent(employeeId)}/educations/${encodeURIComponent(educationId)}`,
  );
}

export type CreateEmployeeExperienceBody = {
  companyName: string;
  designation: string;
  duration?: string;
  additionalNotes?: string;
};

export type EmployeeExperienceApiRow = {
  id: string;
  employeeId: string;
  companyName: string;
  designation: string;
  duration?: string | null;
  additionalNotes?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

export async function addEmployeeExperience(
  employeeId: string,
  body: CreateEmployeeExperienceBody,
): Promise<EmployeeExperienceApiRow> {
  const env = await apiPost<EmployeeExperienceApiRow>(
    `/employees/${encodeURIComponent(employeeId)}/experiences`,
    body,
  );
  return requireApiData(env.data, 'Failed to add experience record');
}

export async function listEmployeeExperiences(
  employeeId: string,
): Promise<EmployeeExperienceApiRow[]> {
  const env = await apiGet<EmployeeExperienceApiRow[]>(
    `/employees/${encodeURIComponent(employeeId)}/experiences`,
  );
  return env.data ?? [];
}

export type UpdateEmployeeExperienceBody = Partial<CreateEmployeeExperienceBody>;

export async function updateEmployeeExperience(
  employeeId: string,
  experienceId: string,
  body: UpdateEmployeeExperienceBody,
): Promise<EmployeeExperienceApiRow> {
  const env = await apiPatch<EmployeeExperienceApiRow>(
    `/employees/${encodeURIComponent(employeeId)}/experiences/${encodeURIComponent(experienceId)}`,
    body,
  );
  return requireApiData(env.data, 'Failed to update experience record');
}

export async function deleteEmployeeExperience(
  employeeId: string,
  experienceId: string,
): Promise<void> {
  await apiDelete(
    `/employees/${encodeURIComponent(employeeId)}/experiences/${encodeURIComponent(experienceId)}`,
  );
}
